import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback, preloadImage } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * `sizes="auto"` is only valid on lazy images; once `loading` is removed Chrome falls back to 100vw
 * and downloads a full-width file for a card-sized image.
 * @param {HTMLImageElement} img
 */
function loadImageEagerly(img) {
  img.removeAttribute('loading');
  img.sizes = img.sizes.replace(/^auto,\s*/, '');
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /** @type {IntersectionObserver | null} */
  #lazyInitObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    // Essential event listeners that need to work immediately
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.navigateToProduct);

    // Preload swatch images immediately - they must always be available
    this.#preloadSwatchImages();

    // Initialize when the card is near the viewport; this preloads the hover image, so doing it for every
    // card on the page downloads dozens of images nobody sees
    this.#lazyInitObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          this.#lazyInitObserver?.unobserve(this);
          this.#initializeNonCriticalFeatures();
        }
      },
      { rootMargin: '200px' } // Start initializing 200px before card is visible
    );
    this.#lazyInitObserver.observe(this);
  }

  /**
   * Preload swatch images immediately - swatches must always be visible
   */
  #preloadSwatchImages() {
    // Find all swatch elements in this product card
    const swatches = this.querySelectorAll('.variant-option__swatch, .swatch');
    
    swatches.forEach(swatch => {
      if (!(swatch instanceof HTMLElement)) return;
      
      // Get the background image URL from CSS custom property
      const swatchBg = getComputedStyle(swatch).getPropertyValue('--swatch-background').trim();
      
      if (swatchBg && swatchBg.startsWith('url(')) {
        // Extract URL from url(...) format
        const imageUrl = swatchBg.slice(4, -1).replace(/['"]/g, '');
        
        if (imageUrl && !imageUrl.startsWith('rgb(') && !imageUrl.startsWith('data:')) {
          // Preload the swatch image
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = imageUrl;
          document.head.appendChild(link);
        }
      }
      
      // Also check for img tags inside swatches (for related colors, etc.)
      const img = swatch.querySelector('img');
      if (img && img.src && !img.complete) {
        // Ensure swatch images load immediately
        img.loading = 'eager';
        if (img.srcset) {
          // Preload the image with srcset
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = img.src;
          // Set imagesrcset attribute (lowercase is correct for HTML attribute)
          link.setAttribute('imagesrcset', img.srcset);
          document.head.appendChild(link);
        } else {
          // Simple preload without srcset
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = img.src;
          document.head.appendChild(link);
        }
      }
    });
  }

  /**
   * Initialize non-critical features that can be deferred
   */
  #initializeNonCriticalFeatures() {
    if (!this.isConnected) return;
    
    this.#handleQuickAdd();
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    if (this.refs.slideshow?.isNested) {
      this.#preloadNextPreviewImage();
    }

    // Preload variant images when card is visible
    this.#preloadVariantImages();
    
    // Prevent videos from playing during previews
    this.#preventVideoAutoplayDuringPreview();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    if (this.#lazyInitObserver) {
      this.#lazyInitObserver.disconnect();
      this.#lazyInitObserver = null;
    }
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const nextImage = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (nextImage instanceof HTMLImageElement) loadImageEagerly(nextImage);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Get mediaId from event if available, otherwise let updateVariantImages find it
    const mediaId = event.detail.resource?.featured_media?.id || null;
    this.updateVariantImages(mediaId);
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * @param {string | null} [mediaId] - Optional media ID to update immediately, bypassing selectedOption lookup.
   */
  updateVariantImages(mediaId = null) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    let selectedImageId = mediaId;
    
    if (!selectedImageId && this.variantPicker?.selectedOption) {
      selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId || null;
    }

    if (!selectedImageId) {
      console.warn('[ProductCard] No mediaId provided and no selectedOption found');
      return;
    }

    const allSlides = slideshow.refs.slides || [];

    // Always pause videos before switching slides to ensure clean transitions
    this.#pauseVideosInSlideshow();

    // Hide video in currently active slide before switching
    // Use slideshow.slides (filtered) with slideshow.current index
    const visibleSlides = slideshow.slides || [];
    const currentSlide = visibleSlides[slideshow.current];
    if (currentSlide) {
      this.#setSlideVideoVisibility(currentSlide, false);
    }

    // Find the target slide and ensure its media is ready
    // Use loose equality (==) in case of string/number type mismatch
    const targetSlide = Array.from(allSlides).find(
      (slide) => slide.getAttribute('slide-id') == selectedImageId
    );

    if (!targetSlide) {
      console.warn('[ProductCard] Target slide not found for media ID:', selectedImageId);
      return;
    }

    // FIRST: Hide all variant slides that don't match BEFORE showing the target
    for (const slide of allSlides) {
      const slideId = slide.getAttribute('slide-id');
      const shouldBeVisible = slideId == selectedImageId;
      const isVariantSlide = slide.getAttribute('variant-image') != null;
      const hasVideo = slide.querySelector('video, deferred-media') !== null;
      
      if (!shouldBeVisible) {
        // Hide non-matching variant slides
        if (isVariantSlide || hasVideo) {
          slide.hidden = true;
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', 'true');
          // Don't use display: none as it prevents slideshow from finding the slide
          // Just use hidden attribute and visibility
          slide.style.visibility = 'hidden';
          this.#setSlideVideoVisibility(slide, false);
        }
      }
    }

    // THEN: Show and prepare the target slide
    targetSlide.removeAttribute('hidden');
    targetSlide.setAttribute('reveal', '');
    targetSlide.setAttribute('aria-hidden', 'false');
    targetSlide.style.visibility = '';
    // Don't set display - let slideshow handle it
    
    // Handle images
    const img = targetSlide.querySelector('img');
    if (img) {
      // Remove lazy loading to ensure immediate load when switching
      if (img.getAttribute('loading') === 'lazy') {
        loadImageEagerly(img);
      }
      // Preload if not already loaded (only when actually needed)
      if (!img.complete || img.naturalWidth === 0) {
        this.#preloadImageByMediaId(selectedImageId);
      }
    }
    
    // Handle videos - ensure they're visible and can play
    const video = targetSlide.querySelector('video, deferred-media');
    if (video && video.hasAttribute('loading') && video.getAttribute('loading') === 'lazy') {
      video.removeAttribute('loading');
    }
    
    // Show videos in the selected slide
    this.#setSlideVideoVisibility(targetSlide, true);

    const isPreviewing = this.hasAttribute('data-previewing-variant');
    
    // FIRST: Remove marker from ALL slides and pause ALL videos
    // Do this immediately to prevent autoplay script from playing wrong videos
    allSlides.forEach(s => {
      s.removeAttribute('data-selected-variant-media');
      // Also pause any videos in these slides immediately
      const video = s.querySelector('video');
      const deferredMedia = s.querySelector('deferred-media');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      if (deferredMedia) {
        try {
          // @ts-ignore - pauseMedia may exist on deferred-media elements
          if (deferredMedia.pauseMedia) deferredMedia.pauseMedia();
        } catch (e) {
          // Ignore errors
        }
      }
    });
    this.#pauseVideosInSlideshow();
    
    // THEN: Mark target slide as selected for autoplay script
    // Only mark it after we've ensured the slideshow will switch
    // We'll set this marker after the slideshow switches
    
    // Immediately select the target slide without delays
    const targetIndex = visibleSlides.indexOf(targetSlide);
    
    // Select the target slide immediately
    if (targetIndex >= 0) {
      slideshow.select(targetIndex, undefined, { animate: false });
    } else {
      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
    
    // Set marker immediately after selection
    requestAnimationFrame(() => {
      const currentSlide = slideshow.slides?.[slideshow.current];
      const currentSlideId = currentSlide?.getAttribute('slide-id');
      
      if (currentSlideId == selectedImageId) {
        targetSlide.setAttribute('data-selected-variant-media', 'true');
        
        // Play video immediately if this slide has one
        const hasVideo = targetSlide.querySelector('video, deferred-media') !== null;
        if (hasVideo && !isPreviewing) {
          this.#playVideoInSlide(targetSlide);
        }
      } else {
        // Fallback: try selecting by index from all slides
        const allSlidesArray = Array.from(slideshow.refs.slides || []);
        const fallbackIndex = allSlidesArray.indexOf(targetSlide);
        if (fallbackIndex >= 0) {
          slideshow.select(fallbackIndex, undefined, { animate: false });
          requestAnimationFrame(() => {
            const verifySlide = slideshow.slides?.[slideshow.current];
            const verifySlideId = verifySlide?.getAttribute('slide-id');
            if (verifySlideId == selectedImageId) {
              targetSlide.setAttribute('data-selected-variant-media', 'true');
              const hasVideo = targetSlide.querySelector('video, deferred-media') !== null;
              if (hasVideo && !isPreviewing) {
                this.#playVideoInSlide(targetSlide);
              }
            }
          });
        }
      }
      
      // If previewing, ensure videos stay paused
      if (isPreviewing) {
        this.#pauseVideosInSlideshow();
      }
    });
  }

  /**
   * Plays video in the specified slide.
   * @param {Element} slide - The slide element.
   */
  #playVideoInSlide(slide) {
    const deferredMedia = slide.querySelector('deferred-media');
    if (deferredMedia instanceof HTMLElement) {
      // Ensure deferred-media is visible
      deferredMedia.style.display = '';
      deferredMedia.style.visibility = '';
      
      // Call playMedia if available (DeferredMedia component method)
      if (typeof /** @type {any} */ (deferredMedia).playMedia === 'function') {
        /** @type {any} */ (deferredMedia).playMedia();
      } else if (typeof /** @type {any} */ (deferredMedia).showDeferredMedia === 'function') {
        // Fallback to showDeferredMedia if playMedia isn't available
        /** @type {any} */ (deferredMedia).showDeferredMedia();
      }
      
      // Also try to play the video element directly
      const video = deferredMedia.querySelector('video:not(template video)');
      if (video instanceof HTMLVideoElement) {
        video.style.display = '';
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('autoplay', '');
        video.play().catch(() => {
          // Autoplay might be blocked, ignore the error
        });
      }
    }
    
    // Handle videos not in deferred-media
    const directVideos = slide.querySelectorAll('video:not(deferred-media video)');
    directVideos.forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.style.display = '';
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('autoplay', '');
        video.play().catch(() => {});
      }
    });
  }

  /**
   * Sets the visibility of video elements within a slide.
   * @param {Element} slide - The slide element.
   * @param {boolean} visible - Whether to show or hide videos.
   */
  #setSlideVideoVisibility(slide, visible) {
    const videos = slide.querySelectorAll('video');
    videos.forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        if (!visible) {
          video.pause();
          video.currentTime = 0;
          video.style.display = 'none';
        } else {
          video.style.display = '';
        }
      }
    });

    const deferredMedia = slide.querySelector('deferred-media');
    if (deferredMedia instanceof HTMLElement) {
      if (!visible) {
        // Call pauseMedia if the deferred-media has it (it's a DeferredMedia instance)
        if (typeof /** @type {any} */ (deferredMedia).pauseMedia === 'function') {
          /** @type {any} */ (deferredMedia).pauseMedia();
        }
        deferredMedia.style.display = 'none';
        
        // Also hide the video inside
        const deferredVideo = deferredMedia.querySelector('video');
        if (deferredVideo instanceof HTMLVideoElement) {
          deferredVideo.pause();
          deferredVideo.currentTime = 0;
          deferredVideo.style.display = 'none';
        }
        
        // Show the preview image that CSS might have hidden due to :has(video) selector
        const productMedia = slide.querySelector('.product-media');
        const previewImage = productMedia?.querySelector('.product-media__image');
        if (previewImage instanceof HTMLElement) {
          previewImage.style.display = '';
          previewImage.style.opacity = '1';
          previewImage.style.visibility = 'visible';
        }
      } else {
        deferredMedia.style.display = '';
        const deferredVideo = deferredMedia.querySelector('video');
        if (deferredVideo instanceof HTMLVideoElement) {
          deferredVideo.style.display = '';
        }
        
        // Let CSS control the preview image visibility when video is visible
        const productMedia = slide.querySelector('.product-media');
        const previewImage = productMedia?.querySelector('.product-media__image');
        if (previewImage instanceof HTMLElement) {
          previewImage.style.display = '';
          previewImage.style.opacity = '';
          previewImage.style.visibility = '';
        }
      }
    }
  }

  /** @type {MutationObserver | null} */
  #videoObserver = null;

  /**
   * Prevents videos from autoplaying during previews by intercepting play attempts.
   * Uses event delegation for better performance.
   */
  #preventVideoAutoplayDuringPreview() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Use event delegation on the slideshow instead of individual listeners
    const handleVideoPlay = (/** @type {Event} */ event) => {
      if (!this.hasAttribute('data-previewing-variant')) return;
      
      const video = event.target;
      if (video instanceof HTMLVideoElement) {
        video.pause();
        video.currentTime = 0;
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Single delegated listener on slideshow (capture phase)
    slideshow.addEventListener('play', handleVideoPlay, true);

    // Only set up mutation observer if there are actually videos
    const hasVideos = slideshow.querySelector('video, deferred-media');
    if (hasVideos) {
      this.#videoObserver = new MutationObserver((mutations) => {
        if (!this.hasAttribute('data-previewing-variant')) return;
        
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLVideoElement) {
              node.addEventListener('play', handleVideoPlay, true);
            } else if (node instanceof HTMLElement) {
              const videos = node.querySelectorAll?.('video');
              if (videos?.length) {
                videos.forEach((video) => video.addEventListener('play', handleVideoPlay, true));
              }
            }
          }
        }
      });

      this.#videoObserver.observe(slideshow, { childList: true, subtree: true });
    }
  }

  /**
   * Pauses all videos in the slideshow.
   */
  #pauseVideosInSlideshow() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Use refs.slides to get ALL slides, including hidden ones
    const allSlides = slideshow.refs.slides || [];
    allSlides.forEach((slide) => {
      // Pause video elements (including those that might be loading)
      const videos = slide.querySelectorAll('video');
      videos.forEach((video) => {
        if (video instanceof HTMLVideoElement) {
          video.pause();
          video.currentTime = 0;
          // Also prevent autoplay by removing autoplay attribute during preview
          if (this.hasAttribute('data-previewing-variant')) {
            video.removeAttribute('autoplay');
          }
        }
      });

      // Pause deferred media videos and prevent their autoplay
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia instanceof HTMLElement) {
        // Call pauseMedia if available
        if (typeof /** @type {any} */ (deferredMedia).pauseMedia === 'function') {
          /** @type {any} */ (deferredMedia).pauseMedia();
        }
        
        // Temporarily remove autoplay attribute during preview
        if (this.hasAttribute('data-previewing-variant') && deferredMedia.hasAttribute('autoplay')) {
          deferredMedia.setAttribute('data-autoplay-temp-disabled', 'true');
          deferredMedia.removeAttribute('autoplay');
        }
        
        const deferredVideo = deferredMedia.querySelector('video');
        if (deferredVideo instanceof HTMLVideoElement) {
          deferredVideo.pause();
          deferredVideo.currentTime = 0;
          deferredVideo.removeAttribute('autoplay');
        }
      }
    });
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {{ controller: AbortController, shown: boolean } | null} */
  #hoverPreview = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
    
    // If we're previewing (swatch hover), pause any videos that might start
    if (this.hasAttribute('data-previewing-variant')) {
      const selectedSlide = event.detail.slide;
      if (selectedSlide) {
        setTimeout(() => {
          const videos = selectedSlide.querySelectorAll('video');
          videos.forEach((video) => {
            if (video instanceof HTMLVideoElement && !video.paused) {
              video.pause();
            }
          });
          
          const deferredMedia = selectedSlide.querySelector('deferred-media');
          if (deferredMedia) {
            const deferredVideo = deferredMedia.querySelector('video');
            if (deferredVideo instanceof HTMLVideoElement && !deferredVideo.paused) {
              deferredVideo.pause();
            }
          }
        }, 10);
      }
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Mark this as a preview to prevent video autoplay BEFORE any switching
    this.setAttribute('data-previewing-variant', 'true');
    
    // Pause any currently playing videos before switching
    this.#pauseVideosInSlideshow();
    
    // Preload the image for this variant before switching
    this.#preloadImageByMediaId(id);

    this.resetVariant.cancel();
    
    // Disable autoplay on all deferred-media elements before switching
    const slides = slideshow.refs.slides || [];
    slides.forEach((slide) => {
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia && deferredMedia.hasAttribute('autoplay')) {
        deferredMedia.setAttribute('data-autoplay-disabled', 'true');
        deferredMedia.removeAttribute('autoplay');
      }
    });
    
    slideshow.select({ id }, undefined, { animate: false });
    
    // Pause videos immediately after switching
    requestAnimationFrame(() => {
      this.#pauseVideosInSlideshow();
      // One additional check after a short delay to catch any delayed autoplay
      setTimeout(() => {
        this.#pauseVideosInSlideshow();
      }, 50);
    });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const hasPreviousSlide = this.#previousSlideIndex != null && this.#previousSlideIndex > 0;
    const targetIndex = hasPreviousSlide ? this.#previousSlideIndex : slideshow.nextIndex;
    // Select by index, not next(): the video preview shows later, and next() from an already-advanced
    // slideshow would wrap back to the first slide
    const showPreview = () => {
      slideshow.select(targetIndex, undefined, { animate: false });
      if (!hasPreviousSlide) setTimeout(() => this.#preloadNextPreviewImage());
    };

    const media = slideshow.slides?.[targetIndex]?.querySelector('deferred-media[data-defer-until-visible]');
    if (!media || !('previewWhenMoving' in media)) {
      showPreview();
      return;
    }

    // Keep the current image until the video is actually moving: its still first frame reads as a stuck video
    this.#hoverPreview?.controller.abort();
    const hoverPreview = { controller: new AbortController(), shown: false };
    this.#hoverPreview = hoverPreview;
    // No timeout fallback: the 1080p file often takes ~1.5s to start, and switching earlier showed a blank slide
    const show = () => {
      if (hoverPreview.shown || hoverPreview.controller.signal.aborted) return;
      hoverPreview.shown = true;
      showPreview();
    };
    media.previewWhenMoving(hoverPreview.controller.signal).then((moved) => moved && show());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    // The pointer left before the video preview was shown, so the slide never changed
    const previewPending = this.#hoverPreview != null && !this.#hoverPreview.shown;
    this.#hoverPreview?.controller.abort();
    this.#hoverPreview = null;

    if (!this.variantPicker) {
      if (!slideshow || previewPending) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Pause any playing videos from the preview first
    this.#pauseVideosInSlideshow();
    
    // Restore autoplay attributes that were temporarily disabled
    const slides = slideshow.refs.slides || [];
    slides.forEach((slide) => {
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia) {
        if (deferredMedia.hasAttribute('data-autoplay-temp-disabled')) {
          deferredMedia.removeAttribute('data-autoplay-temp-disabled');
          deferredMedia.setAttribute('autoplay', '');
        }
        if (deferredMedia.hasAttribute('data-autoplay-disabled')) {
          deferredMedia.removeAttribute('data-autoplay-disabled');
          deferredMedia.setAttribute('autoplay', '');
        }
      }
    });
    
    // Remove preview flag
    this.removeAttribute('data-previewing-variant');

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * Preloads variant images when user shows intent (hover).
   * Uses a conservative approach to minimize bandwidth usage.
   */
  #preloadVariantImages() {
    // Check if user has data saver enabled
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData) {
      return;
    }

    const variantSwatches = this.querySelectorAll('input[data-option-media-id]');
    
    // Only preload on hover (user intent) to save bandwidth
    variantSwatches.forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;
      
      const label = swatch.closest('label');
      if (!label) return;

      // Preload on hover with a small delay to avoid unnecessary loads
      /** @type {ReturnType<typeof setTimeout> | null} */
      let hoverTimeout = null;
      label.addEventListener('pointerenter', () => {
        hoverTimeout = setTimeout(() => {
          const mediaId = swatch.dataset.optionMediaId;
          if (mediaId) {
            this.#preloadImageByMediaId(mediaId);
          }
        }, 50);
      });

      label.addEventListener('pointerleave', () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
      });
    });
  }

  /**
   * Preloads an image by its media ID.
   * @param {string | null} mediaId - The media ID to preload.
   */
  #preloadImageByMediaId(mediaId) {
    if (!mediaId) return;
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slide = Array.from(slideshow.refs.slides || []).find(
      (s) => s.getAttribute('slide-id') === mediaId
    );

    if (!slide) return;

    const img = slide.querySelector('img');
    if (!img) return;

    // Remove loading="lazy" to allow immediate loading
    if (img.getAttribute('loading') === 'lazy') {
      loadImageEagerly(img);
    }

    // Get the image URL from various sources
    let imageUrl = img.currentSrc || img.src;
    
    // If image hasn't loaded yet, try to get URL from srcset or data attributes
    if (!imageUrl || imageUrl === '' || imageUrl.includes('data:image/svg')) {
      // Try data-max-resolution first (high quality)
      const maxRes = img.dataset.maxResolution;
      if (maxRes) {
        imageUrl = maxRes;
      } else if (img.srcset) {
        // Fall back to srcset if available
        const srcsetEntries = img.srcset.split(',');
        // Get the largest image from srcset
        const largest = srcsetEntries[srcsetEntries.length - 1];
        const srcsetUrl = largest?.trim().split(' ')[0];
        if (srcsetUrl) {
          imageUrl = srcsetUrl;
        }
      }
      
      // Fall back to src if still no URL
      if (!imageUrl || imageUrl === '') {
        imageUrl = img.src;
      }
    }

    // Preload the image if we have a valid URL
    if (imageUrl && imageUrl !== '' && !imageUrl.startsWith('data:')) {
      preloadImage(imageUrl);
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Immediately update the image if we have a media ID (optimistic update)
    const mediaId = clickedSwatch.dataset.optionMediaId;
    if (mediaId && this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.updateVariantImages(mediaId);
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
